import React, { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Form, Input, Button, Card, Row, Col, DatePicker, InputNumber,
  Breadcrumb, Grid, message, Typography, AutoComplete, Tag, Divider,
  Alert, Spin, Empty
} from 'antd';
import {
  RightOutlined, WarningOutlined, SaveOutlined, SearchOutlined,
  FileProtectOutlined, CalendarOutlined, EnvironmentOutlined,
  UserOutlined, BankOutlined, CarOutlined, PhoneOutlined,
  InfoCircleOutlined, ArrowLeftOutlined, ExclamationCircleOutlined,
  FileTextOutlined, DollarOutlined, TeamOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { sinistresService, contratsService, settingsService } from '../../common/services';
import { AuthContext } from '../../common/constants';
import { formatMontant as formatMontantShared, formatPhone } from '../../common/utils/format';

const formatMontant = (val) => formatMontantShared(val, { suffix: ' FCFA', fallback: 'N/A' });

const { TextArea } = Input;
const { Text, Title } = Typography;

const FALLBACK_TYPES = [
  { code: 'accident', label: 'Accident de circulation' },
  { code: 'vol', label: 'Vol' },
  { code: 'incendie', label: 'Incendie' },
  { code: 'bris_glace', label: 'Bris de glace' },
  { code: 'degat_eaux', label: 'Degat des eaux' },
  { code: 'catastrophe_naturelle', label: 'Catastrophe naturelle' },
  { code: 'responsabilite_civile', label: 'Responsabilite civile' },
  { code: 'dommages_corporels', label: 'Dommages corporels' },
  { code: 'autre', label: 'Autre' },
];

const FALLBACK_CIRCONSTANCES = [
  { code: 'en_circulation', label: 'En circulation' },
  { code: 'en_stationnement', label: 'En stationnement' },
  { code: 'au_domicile', label: 'Au domicile' },
  { code: 'sur_lieu_travail', label: 'Sur le lieu de travail' },
  { code: 'autre', label: 'Autre' },
];

const moment = require('moment');
// antd v5 = dayjs : on convertit aux frontières du DatePicker (le form reste en moment).
const dayjs = require('dayjs');
const toDayjs = (m) => (m && m.valueOf ? dayjs(m.valueOf()) : null);
const toMoment = (d) => (d && d.valueOf ? moment(d.valueOf()) : null);

const NouveauSinistre = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditMode = !!editId;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  const { currentUser } = useContext(AuthContext);

  // Données dynamiques
  const [typesSinistre, setTypesSinistre] = useState(FALLBACK_TYPES);
  const [circonstances, setCirconstances] = useState(FALLBACK_CIRCONSTANCES);

  // Recherche contrat
  const [searchLoading, setSearchLoading] = useState(false);
  const [contratOptions, setContratOptions] = useState([]);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [initialLoading, setInitialLoading] = useState(false);

  // Charger le sinistre existant en mode edition
  useEffect(() => {
    if (!editId) return;
    const loadSinistre = async () => {
      try {
        setInitialLoading(true);
        const res = await sinistresService.getById(editId);
        const s = res?.data || res;
        if (s) {
          // Charger le contrat complet
          if (s.contrat) {
            const contratData = typeof s.contrat === 'object' ? s.contrat : null;
            if (contratData) {
              setSelectedContrat({ ...contratData, client: s.client, agence: s.agence });
              setSearchText(contratData.numeroPolice || '');
              form.setFieldsValue({ contrat: contratData._id });
            }
          }
          form.setFieldsValue({
            typeSinistre: s.typeSinistre,
            dateSinistre: s.dateSinistre ? moment(s.dateSinistre) : undefined,
            lieu: s.lieu,
            montantEstime: s.montantEstime,
            tiersConcerne: s.tiersConcerne,
            circonstances: s.circonstances,
            description: s.description,
          });
        }
      } catch (err) {
        toast.error('Erreur lors du chargement du sinistre');
      } finally {
        setInitialLoading(false);
      }
    };
    loadSinistre();
  }, [editId]);

  // Pré-sélection contrat depuis paramètre URL (?contrat=ID)
  useEffect(() => {
    if (editId) return; // mode édition gère déjà le contrat
    const contratParam = searchParams.get('contrat');
    if (!contratParam || selectedContrat) return;
    (async () => {
      try {
        setInitialLoading(true);
        const res = await contratsService.getById(contratParam);
        const contrat = res?.data || res;
        if (contrat) {
          setSelectedContrat(contrat);
          setSearchText(contrat.numeroPolice || '');
          form.setFieldsValue({ contrat: contrat._id });
        }
      } catch (err) {
        toast.error('Impossible de charger le contrat sélectionné');
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [searchParams, editId, selectedContrat, form]);

  // Charger les parametres dynamiques
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await settingsService.getAll('sinistres');
        const raw = res?.data || res;
        const list = raw?.data || raw;
        if (Array.isArray(list)) {
          list.forEach(s => {
            if (s.key === 'TYPES_SINISTRE' && Array.isArray(s.value) && s.value.length > 0) {
              setTypesSinistre(s.value);
            }
            if (s.key === 'CIRCONSTANCES_SINISTRE' && Array.isArray(s.value) && s.value.length > 0) {
              setCirconstances(s.value);
            }
          });
        }
      } catch (err) {
        console.warn('Chargement parametres sinistres echoue, utilisation des valeurs par defaut');
      }
    };
    loadSettings();
  }, []);

  const searchContrats = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setContratOptions([]);
      return;
    }
    try {
      setSearchLoading(true);
      const res = await contratsService.getAll({ q: query, limit: 10, statut: 'actif' });
      const data = res?.data || res;
      const items = data?.items || [];
      setContratOptions(items.map(c => ({
        value: c._id,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
            <div>
              <Text strong style={{ color: '#1A2B5F' }}>{c.numeroPolice}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {c.client?.nom} {c.client?.prenom || ''} — {c.compagnie?.nom || ''}
              </Text>
            </div>
            <Tag color="green" style={{ marginLeft: 8 }}>Actif</Tag>
          </div>
        ),
        contrat: c,
      })));
    } catch (err) {
      console.error('Erreur recherche contrats:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSelectContrat = (value, option) => {
    const contrat = option.contrat;
    setSelectedContrat(contrat);
    setSearchText(contrat.numeroPolice);
    form.setFieldsValue({ contrat: contrat._id });
  };

  const handleClearContrat = () => {
    setSelectedContrat(null);
    setSearchText('');
    form.setFieldsValue({ contrat: undefined });
  };

  const handleSubmit = async (values) => {
    if (!selectedContrat) {
      toast.warning('Veuillez sélectionner un contrat valide');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        contrat: selectedContrat._id,
        client: selectedContrat.client?._id,
        agence: selectedContrat.agence?._id || currentUser?.agence,
        dateSinistre: values.dateSinistre?.toISOString(),
        typeSinistre: values.typeSinistre,
        description: values.description,
        montantEstime: values.montantEstime || undefined,
        lieu: values.lieu || undefined,
        tiersConcerne: values.tiersConcerne || undefined,
        circonstances: values.circonstances || undefined,
      };

      if (isEditMode) {
        await sinistresService.update(editId, payload);
        toast.success('Sinistre modifie avec succès');
      } else {
        await sinistresService.create(payload);
        toast.success('Sinistre declare avec succès');
      }
      navigate('/dashboard/sinistres');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la declaration du sinistre');
    } finally {
      setLoading(false);
    }
  };

  const iconBoxStyle = (bg) => ({
    width: 36, height: 36, borderRadius: 10, background: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: 16, flexShrink: 0,
  });

  const cardStyle = {
    borderRadius: 14, marginBottom: 20,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #f0f0f0', overflow: 'hidden',
  };

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: isMobile ? 12 : 24, paddingRight: isMobile ? 12 : 24, paddingBottom: 24 }}>

        {/* Breadcrumb */}
        <Breadcrumb separator={<RightOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />} style={{ marginBottom: 16 }}>
          <Breadcrumb.Item><Link to="/dashboard" style={{ color: '#1A2B5F' }}>Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/sinistres" style={{ color: '#1A2B5F' }}>Sinistres</Link></Breadcrumb.Item>
          <Breadcrumb.Item><span style={{ color: '#595959' }}>{isEditMode ? 'Modifier' : 'Nouvelle declaration'}</span></Breadcrumb.Item>
        </Breadcrumb>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #1A2B5F 0%, #2A3E7A 100%)',
          borderRadius: 14, padding: isMobile ? '20px 16px' : '24px 28px',
          marginBottom: 24, color: '#fff',
          display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
          gap: 16, flexDirection: isMobile ? 'column' : 'row',
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, flexShrink: 0,
          }}>
            <ExclamationCircleOutlined />
          </div>
          <div style={{ flex: 1 }}>
            <Title level={4} style={{ color: '#fff', margin: 0, fontSize: isMobile ? 18 : 20 }}>
              {isEditMode ? 'Modifier le sinistre' : 'Déclaration de sinistre'}
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
              {isEditMode ? 'Modifiez les informations du sinistre' : 'Remplissez le formulaire ci-dessous pour declarer un nouveau sinistre sur un contrat actif'}
            </Text>
          </div>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/dashboard/sinistres')}
            style={{
              borderRadius: 10, border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', background: 'rgba(255,255,255,0.1)',
            }}
          >
            {!isMobile && 'Retour'}
          </Button>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit} size={isMobile ? 'middle' : 'large'} requiredMark="optional">

          {/* SECTION 1 : RECHERCHE CONTRAT */}
          <Card style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 0 }}>
              <div style={iconBoxStyle('linear-gradient(135deg, #1A2B5F, #2A3E7A)')}>
                <FileProtectOutlined />
              </div>
              <div>
                <Text strong style={{ fontSize: 15, color: '#1A2B5F' }}>Contrat concerne</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>Recherchez le contrat par numéro de police, nom du client ou telephone</Text>
              </div>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Champ cache pour stocker l'ID du contrat dans le formulaire */}
            <Form.Item name="contrat" hidden rules={[{ required: true, message: 'Veuillez sélectionner un contrat' }]}>
              <Input type="hidden" />
            </Form.Item>

            <div style={{ marginBottom: selectedContrat ? 16 : 0 }}>
              <AutoComplete
                options={contratOptions}
                onSearch={searchContrats}
                onSelect={handleSelectContrat}
                value={searchText}
                onChange={(val) => {
                  setSearchText(val);
                  if (!val) handleClearContrat();
                }}
                allowClear
                onClear={handleClearContrat}
                style={{ width: '100%' }}
                notFoundContent={
                  searchLoading ? (
                    <div style={{ textAlign: 'center', padding: 16 }}><Spin size="small" /><br /><Text type="secondary" style={{ fontSize: 12 }}>Recherche en cours...</Text></div>
                  ) : searchText && searchText.length >= 2 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun contrat actif trouve" />
                  ) : null
                }
              >
                <Input
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="Tapez un numéro de police, nom ou telephone..."
                  style={{ borderRadius: 10, height: 44 }}
                  suffix={searchLoading ? <Spin size="small" /> : null}
                />
              </AutoComplete>
              {!selectedContrat && form.isFieldTouched('contrat') && (
                <Text type="danger" style={{ fontSize: 12 }}>Veuillez sélectionner un contrat</Text>
              )}
            </div>

            {/* Recapitulatif contrat selectionne */}
            {selectedContrat && (
              <div style={{
                background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
                borderRadius: 12, padding: isMobile ? 14 : 20,
                border: '1px solid #d6e4ff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileProtectOutlined style={{ color: '#1A2B5F', fontSize: 16 }} />
                    <Text strong style={{ color: '#1A2B5F', fontSize: 15 }}>
                      Police N° {selectedContrat.numeroPolice}
                    </Text>
                  </div>
                  <Tag color="green">Contrat actif</Tag>
                </div>

                <Row gutter={[16, 12]}>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <UserOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Souscripteur</Text>
                        <Text strong style={{ fontSize: 13 }}>
                          {selectedContrat.client?.nom} {selectedContrat.client?.prenom || ''}
                        </Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <PhoneOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Téléphone</Text>
                        <Text strong style={{ fontSize: 13 }}>{formatPhone(selectedContrat.client?.telephone)}</Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <BankOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Compagnie</Text>
                        <Text strong style={{ fontSize: 13 }}>{selectedContrat.compagnie?.nom || 'N/A'}</Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileTextOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Type assurance</Text>
                        <Text strong style={{ fontSize: 13 }}>{selectedContrat.typeAssurance?.nom || 'N/A'}</Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CalendarOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Période</Text>
                        <Text strong style={{ fontSize: 13 }}>
                          {selectedContrat.dateEffet ? new Date(selectedContrat.dateEffet).toLocaleDateString('fr-FR') : '?'}
                          {' → '}
                          {selectedContrat.dateEcheance ? new Date(selectedContrat.dateEcheance).toLocaleDateString('fr-FR') : '?'}
                        </Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DollarOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Prime TTC</Text>
                        <Text strong style={{ fontSize: 13, color: '#1A2B5F' }}>{formatMontant(selectedContrat.primeTTC)}</Text>
                      </div>
                    </div>
                  </Col>
                  {selectedContrat.immatriculation && (
                    <Col xs={24} sm={12} md={8}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CarOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                        <div>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Véhicule</Text>
                          <Text strong style={{ fontSize: 13 }}>
                            {selectedContrat.marqueVehicule || ''} — {selectedContrat.immatriculation}
                          </Text>
                        </div>
                      </div>
                    </Col>
                  )}
                  {selectedContrat.agence && (
                    <Col xs={24} sm={12} md={8}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <EnvironmentOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                        <div>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Agence</Text>
                          <Text strong style={{ fontSize: 13 }}>{selectedContrat.agence?.nom || 'N/A'}</Text>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </Card>

          {/* SECTION 2 : DETAILS DU SINISTRE */}
          <Card style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 0 }}>
              <div style={iconBoxStyle('linear-gradient(135deg, #fa541c, #fa8c16)')}>
                <WarningOutlined />
              </div>
              <div>
                <Text strong style={{ fontSize: 15, color: '#1A2B5F' }}>Détails du sinistre</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>Informations sur l'evenement survenu</Text>
              </div>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="typeSinistre"
                  label={<span><WarningOutlined style={{ marginRight: 6, color: '#fa8c16' }} />Type de sinistre</span>}
                  rules={[{ required: true, message: 'Veuillez sélectionner ou saisir le type de sinistre' }]}
                >
                  <AutoComplete
                    options={typesSinistre.map(t => ({ value: t.label }))}
                    placeholder="Tapez ou sélectionnez un type..."
                    style={{ borderRadius: 10 }}
                    size="large"
                    filterOption={(input, option) =>
                      (option?.value || '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="dateSinistre"
                  label={<span><CalendarOutlined style={{ marginRight: 6, color: '#1890ff' }} />Date du sinistre</span>}
                  getValueProps={(v) => ({ value: toDayjs(v) })}
                  normalize={(v) => toMoment(v)}
                  rules={[
                    { required: true, message: 'Veuillez indiquer la date du sinistre' },
                    () => ({
                      validator(_, value) {
                        if (!value) return Promise.resolve();
                        // Comparaisons par timestamp (compatibles moment ET dayjs)
                        const t = value.valueOf();
                        // Date dans le futur interdite
                        if (t > moment().endOf('day').valueOf()) {
                          return Promise.reject(new Error('La date ne peut pas être dans le futur'));
                        }
                        // Avant l'effet du contrat (non couvert)
                        if (selectedContrat?.dateEffet && t < moment(selectedContrat.dateEffet).startOf('day').valueOf()) {
                          return Promise.reject(new Error(`Le sinistre ne peut pas être antérieur à la date d'effet du contrat (${moment(selectedContrat.dateEffet).format('DD/MM/YYYY')})`));
                        }
                        // Après l'échéance (déjà expiré au moment du sinistre)
                        if (selectedContrat?.dateEcheance && t > moment(selectedContrat.dateEcheance).endOf('day').valueOf()) {
                          return Promise.reject(new Error(`Le sinistre est postérieur à l'échéance du contrat (${moment(selectedContrat.dateEcheance).format('DD/MM/YYYY')})`));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%', borderRadius: 10, height: 44 }}
                    format="DD/MM/YYYY"
                    placeholder="JJ/MM/AAAA"
                    size="large"
                    disabledDate={(current) => {
                      if (!current) return false;
                      const t = current.valueOf();
                      // Pas dans le futur
                      if (t > moment().endOf('day').valueOf()) return true;
                      // Avant l'effet du contrat sélectionné
                      if (selectedContrat?.dateEffet && t < moment(selectedContrat.dateEffet).startOf('day').valueOf()) return true;
                      // Après l'échéance
                      if (selectedContrat?.dateEcheance && t > moment(selectedContrat.dateEcheance).endOf('day').valueOf()) return true;
                      return false;
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="lieu"
                  label={<span><EnvironmentOutlined style={{ marginRight: 6, color: '#52c41a' }} />Lieu du sinistre</span>}
                >
                  <Input placeholder="Ex: Cocody, Carrefour Palmeraie" style={{ borderRadius: 10, height: 44 }} size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="montantEstime"
                  label={<span><DollarOutlined style={{ marginRight: 6, color: '#722ed1' }} />Montant estime</span>}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%', borderRadius: 10, height: 44 }}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                    parser={v => v.replace(/\s/g, '')}
                    placeholder="Montant en FCFA"
                    addonAfter="FCFA"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="tiersConcerne"
                  label={<span><TeamOutlined style={{ marginRight: 6, color: '#eb2f96' }} />Tiers concerne</span>}
                >
                  <Input placeholder="Nom et coordonnees du tiers" style={{ borderRadius: 10, height: 44 }} size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="circonstances"
                  label={<span><InfoCircleOutlined style={{ marginRight: 6, color: '#1890ff' }} />Circonstances</span>}
                >
                  <AutoComplete
                    options={circonstances.map(c => ({ value: c.label }))}
                    placeholder="Tapez ou sélectionnez..."
                    style={{ borderRadius: 10 }}
                    size="large"
                    allowClear
                    filterOption={(input, option) =>
                      (option?.value || '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* SECTION 3 : DESCRIPTION */}
          <Card style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 0 }}>
              <div style={iconBoxStyle('linear-gradient(135deg, #1890ff, #36cfc9)')}>
                <FileTextOutlined />
              </div>
              <div>
                <Text strong style={{ fontSize: 15, color: '#1A2B5F' }}>Description detaillee</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>Decrivez les circonstances et les details de l'evenement</Text>
              </div>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <Form.Item
              name="description"
              rules={[{ required: true, message: 'Veuillez decrire le sinistre' }]}
            >
              <TextArea
                rows={5}
                placeholder="Decrivez en detail ce qui s'est passe : circonstances, degats constates, personnes impliquees, temoins eventuels..."
                style={{ borderRadius: 10, resize: 'vertical' }}
                showCount
                maxLength={2000}
              />
            </Form.Item>

            <Alert
              message="Conseil"
              description="Soyez le plus precis possible dans votre description. Mentionnez la date, l'heure, le lieu exact, les circonstances et les dommages constates."
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ borderRadius: 10, marginTop: 4 }}
            />
          </Card>

          {/* BOUTONS D'ACTION */}
          <div style={{
            display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end',
            gap: 12, flexDirection: isMobile ? 'column-reverse' : 'row', padding: '4px 0',
          }}>
            <Button size="large" onClick={() => navigate('/dashboard/sinistres')}
              style={{ borderRadius: 10, width: isMobile ? '100%' : 'auto', height: 46, fontWeight: 500 }}>
              Annuler
            </Button>
            <Button
              type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}
              size="large" disabled={!selectedContrat}
              style={{
                borderRadius: 10, width: isMobile ? '100%' : 'auto',
                minWidth: isMobile ? '100%' : 220, height: 46, fontWeight: 600,
                background: selectedContrat ? 'linear-gradient(135deg, #C8102E 0%, #e8384f 100%)' : undefined,
                border: 'none',
                boxShadow: selectedContrat ? '0 4px 14px rgba(200, 16, 46, 0.35)' : undefined,
              }}
            >
              {loading ? (isEditMode ? 'Enregistrement...' : 'Déclaration en cours...') : (isEditMode ? 'Enregistrer les modifications' : 'Declarer le sinistre')}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default NouveauSinistre;
